import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import H3 from "../../../atoms/typography/H3"
import { theme } from "../../../theme"
import { HorizontalSpacer } from "../../../atoms/spacers"
import { ActiveItemBar } from "../../../atoms/ActiveItemColoredBar"
import H1 from "../../../atoms/typography/H1"
import Body from "../../../atoms/typography/Body"
import {
  AligningTerminal,
  AzimuthingTerminal,
  GlobeBoxSatcube,
  HandHoldingPhone,
  ReturnSatcubePack,
  SatcubeComputer,
} from "../../../atoms/icons/UspIcons/UspIcons"

interface Props {
  color: string
}

interface SetupStepItemProps {
  active: boolean
  text: string
  gradient: string
  onClick: () => void
}

const ColoredTitle = styled(H1)<{ gradient: string }>`
  background: ${(props) => props.gradient};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`

const StepItemContainer = styled.div`
  width: 224px;
  margin: 20px;
  padding: 20px;
  cursor: pointer;

  :hover {
    filter: opacity(0.6);
  }
`

const SetUpInstructionContainer = styled.div`
  display: flex;
  align-items: center;
`

const SetUpIconContainer = styled.div`
  width: 110px;
  height: 135px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 32px;
`
const SpecifiedCols = styled(Col)`
  padding-left: 36px;
  padding-right: 36px;
  height: 600px;
  width: 300px;
`

const SetupStepItem: React.FC<SetupStepItemProps> = ({ active, text, gradient, onClick }) => {
  return (
    <StepItemContainer>
      <H3 color={active ? theme.colors.greyDark : theme.colors.greyDisabled} textAlign="center" onClick={onClick}>
        {text}
      </H3>
      <HorizontalSpacer spacing={1} />
      <ActiveItemBar color={active ? gradient : theme.colors.greyDisabled} />
    </StepItemContainer>
  )
}

const PartTwo: React.FC<Props> = ({ color }) => {
  return (
    <div>
      <ColoredTitle gradient={color}>Support around the clock</ColoredTitle>
      <HorizontalSpacer spacing={1} />
      <SetUpInstructionContainer>
        <SetUpIconContainer>
          <HandHoldingPhone />
        </SetUpIconContainer>
        <div>
          <Body>Satcube takes care of onboarding through the media of your choice.</Body>
        </div>
      </SetUpInstructionContainer>
      <SetUpInstructionContainer>
        <SetUpIconContainer>
          <AligningTerminal />
        </SetUpIconContainer>
        <div>
          <Body>We teach you all there is to know about how to use the Satcube Ku.</Body>
        </div>
      </SetUpInstructionContainer>
    </div>
  )
}

const PartOne: React.FC<Props> = ({ color }) => {
  return (
    <div>
      <ColoredTitle gradient={color}>Fast global delivery</ColoredTitle>
      <HorizontalSpacer spacing={1} />
      <SetUpInstructionContainer>
        <SetUpIconContainer>
          <SatcubeComputer />
        </SetUpIconContainer>
        <div>
          <Body>Order your terminal online.</Body>
        </div>
      </SetUpInstructionContainer>
      <SetUpInstructionContainer>
        <SetUpIconContainer>
          <GlobeBoxSatcube />
        </SetUpIconContainer>
        <div>
          <Body>Satcube ships the terminal to you, wherever you are in the world, within 2 business days.</Body>
        </div>
      </SetUpInstructionContainer>
    </div>
  )
}
const PartThree: React.FC<Props> = ({ color }) => {
  return (
    <div>
      <ColoredTitle gradient={color}>Seamless connection</ColoredTitle>
      <HorizontalSpacer spacing={1} />
      <SetUpInstructionContainer>
        <SetUpIconContainer>
          <AzimuthingTerminal />
        </SetUpIconContainer>
        <div>
          <Body>Use the Satcube terminal to connect to the internet, anywhere in the world.</Body>
        </div>
      </SetUpInstructionContainer>
      <SetUpInstructionContainer>
        <SetUpIconContainer>
          <ReturnSatcubePack />
        </SetUpIconContainer>
        <div>
          <Body>Once you are done using the terminal, Satcube takes care of the pick-up and return.</Body>
        </div>
      </SetUpInstructionContainer>
    </div>
  )
}

const UspCarouselInstant: React.FC<Props> = ({ color }) => {
  const [activeSetupStep, setActiveSetupStep] = React.useState(0)
  const [stopInterval, setStopInterval] = React.useState(false)

  const onClickStep = (stepNumber: number) => {
    setActiveSetupStep(stepNumber)
    setStopInterval(true)
  }

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      if (!stopInterval) {
        setActiveSetupStep((prevStep) => (prevStep + 1) % 3)
      }
    }, 6000)

    if (stopInterval) {
      clearInterval(intervalId)
    }

    return () => {
      clearInterval(intervalId)
    }
  }, [stopInterval])

  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "row",
        justifyContent: "between",
        alignItems: "center",
      }}
    >
      <Hidden xs sm md>
        <SpecifiedCols lg={3}>
          <SetupStepItem
            text="Order & delivery"
            active={activeSetupStep === 0}
            gradient={color}
            onClick={() => onClickStep(0)}
          />
          <SetupStepItem
            text="Introduction"
            active={activeSetupStep === 1}
            gradient={color}
            onClick={() => onClickStep(1)}
          />
          <SetupStepItem
            text="Connect"
            active={activeSetupStep === 2}
            gradient={color}
            onClick={() => onClickStep(2)}
          />
        </SpecifiedCols>
        <Col lg={1} />
      </Hidden>
      <div>
        <Visible xs>
          <Row justify="around">
            <div style={{ width: "15%" }}>
              <ActiveItemBar
                color={activeSetupStep === 0 ? color : theme.colors.greyDark}
                onClick={() => onClickStep(0)}
              />
            </div>
            <div style={{ width: "15%" }}>
              <ActiveItemBar
                color={activeSetupStep === 1 ? color : theme.colors.greyDark}
                onClick={() => onClickStep(1)}
              />
            </div>
            <div style={{ width: "15%" }}>
              <ActiveItemBar
                color={activeSetupStep === 2 ? color : theme.colors.greyDark}
                onClick={() => onClickStep(2)}
              />
            </div>
          </Row>

          <HorizontalSpacer spacing={4} />
        </Visible>

        <SpecifiedCols lg={9} md={8} sm={12}>
          {activeSetupStep === 0 && <PartOne color={color} />}
          {activeSetupStep === 1 && <PartTwo color={color} />}
          {activeSetupStep === 2 && <PartThree color={color} />}
        </SpecifiedCols>
      </div>
    </div>
  )
}

export default UspCarouselInstant
